import React, { useState } from 'react';
import { Grid, Stack } from '@mui/material';
import Button from 'components/Button';
import Select from 'components/Select';
import TextField from 'components/TextField';
import { useTemplateTask } from 'pages/template-task/layouts';
import { ITemplateTask } from '@data/models/template-task.model';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import CheckboxSingle from 'components/CheckboxSingle';
import { toast } from 'react-toastify';
import { DASHBOARD_ROUTES } from 'Routes';
import { buildApiPath, formatterToCaps } from 'utils';
import {
  createTemplateTask,
  removeTemplateTask,
  updateTemplateTask,
} from 'data/api/template-task.api';
import { AxiosError, AxiosResponse } from 'axios';
import { API_ROUTES } from 'data/ApiRoutes';
import { GET } from 'data/services/api';
import RichTextEditorField from 'components/RichTextEditorField';
import { useSelector } from 'react-redux';
import { selectTemplateTaskList } from 'store/templateTaskListSlide';
import DOMPurify from 'dompurify';

export enum TaskStatusEnum {
  NotStarted = 'Not Started',
  Queued = 'Queued',
  InProgress = 'In Progress',
  SentComplete = 'Sent/Complete',
  WillNotSendDo = 'Will not send/do',
}

export enum MailTypeEnum {
  USPSFirstClass = 'USPS First Class',
  USPSStandard = 'USPS Standard',
}

interface CommunicationTemplate {
  id: string;
  templateName?: string | undefined;
  templateType?: string | undefined;
}
interface CommunicationTemplateListResponse {
  communicationTemplates: CommunicationTemplate[] | null;
}

export const DetailFormTask = () => {
  const [communicationTemplates, setCommunicationTemplates] =
    useState<CommunicationTemplateListResponse>();
  const { templateTask } = useTemplateTask();
  const navigate = useNavigate();
  const { control, handleSubmit, reset, clearErrors, watch, getValues } =
    useForm<ITemplateTask>({
      reValidateMode: 'onChange',
    });
  const templateTaskList = useSelector(selectTemplateTaskList);

  const resetForm = (dueDateLogic: string) => {
    reset({
      id: templateTask?.id,
      templateTaskListsId: templateTaskList?.id || '',
      timeCategory: templateTask?.timeCategory || '',
      taskName: templateTask?.taskName || '',
      assignedToLogic: templateTask?.assignedToLogic || '',
      dueDateLogic: templateTask?.dueDateLogic || dueDateLogic,
      detail: templateTask?.detail || '',
      orderIndex: templateTask?.orderIndex || 0,
      templateTaskType: templateTask?.templateTaskType || '',
      templateTaskStatus: templateTask?.templateTaskStatus || '',
      sendTo: templateTask?.sendTo || '',
      mailType: templateTask?.mailType || '',
      communicationTemplate: templateTask?.communicationTemplate || '',
      taskCategory: templateTask?.taskCategory || '',
      showFindProviderCardLarge:
        templateTask?.showFindProviderCardLarge || false,
      dueDateType: templateTask?.dueDateType || '',
    });
    clearErrors();
  };

  const getCommunicationTemplates = async (templateType: string) => {
    try {
      const data: AxiosResponse<CommunicationTemplateListResponse> = await GET(
        buildApiPath({
          path: `${API_ROUTES.getCommunicationTemplates}`,
          params: {
            templateType,
          },
        }),
      );
      if (data.data) {
        setCommunicationTemplates(data.data);
      }
    } catch (e: unknown) {
      if (typeof e === 'string') {
        throw Error(e.toUpperCase());
      } else if (e instanceof Error) {
        throw Error(e.message);
      }
      throw Error('');
    }
  };

  const handleAllValid = async (values: any) => {
    try {
      if (values.detail) {
        values.detail = DOMPurify.sanitize(values.detail);
      }
      let result;
      const request = {
        ...values,
        templateTaskListsId: templateTaskList?.id || '',
        orderIndex: parseInt(values?.orderIndex),
      };
      if (!templateTask?.id) {
        result = await createTemplateTask(request);
      } else {
        result = await updateTemplateTask(request);
      }
      if (!result?.success) {
        throw new Error(result?.message);
      }
      toast.success(
        !templateTask?.id
          ? 'Add template task successfully'
          : 'Update template task successfully',
      );
      navigate(
        `${DASHBOARD_ROUTES.LIST_TASK}`.replace(
          ':templateTaskListId',
          `${templateTaskList?.id}`,
        ),
      );
    } catch (err) {
      const error = err as Error | AxiosError;
      if (templateTask?.id) {
        toast.error(
          formatterToCaps(error.message) || 'Update template task failed!',
        );
      } else {
        toast.error(
          formatterToCaps(error.message) || 'Create template task failed!',
        );
      }
      console.error('Error: ', error);
    }
  };

  const handleRemove = async () => {
    try {
      const res = await removeTemplateTask(templateTask?.id || '');
      if (res) {
        toast.success('Remove template task successfully');
        navigate(
          `${DASHBOARD_ROUTES.LIST_TASK}`.replace(
            ':templateTaskListId',
            `${templateTaskList?.id}`,
          ),
        );
      }
    } catch (error) {
      toast.error(
        `Oops! The action can't be done because this template tasklist has been using by some other tasks`,
      );
    }
  };

  React.useEffect(() => {
    resetForm('0');
  }, [templateTask]);

  React.useEffect(() => {
    getCommunicationTemplates(getValues('templateTaskType') ?? '');
  }, [watch('templateTaskType')]);

  return (
    <form onSubmit={handleSubmit(handleAllValid)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack
            direction={{ sm: 'row', xs: 'column-reverse' }}
            justifyContent="end"
            alignItems="center"
            width="full"
            spacing={1}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                width: { xs: '100%', sm: 120 },
              }}
            >
              Save
            </Button>
            {templateTask?.id && (
              <Button
                variant="contained"
                color="error"
                sx={{
                  width: { xs: '100%', sm: 120 },
                }}
                onClick={handleRemove}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CheckboxSingle name="showFindProviderCardLarge" control={control} />
          Show Find Provider Card Large
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            name="taskName"
            label="Task Name"
            control={control}
            required
            rules={{ required: 'Task Name is required.' }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="templateTaskType"
            control={control}
            label="Template Type"
            showSelected={true}
            required
            rules={{ required: 'Template Type is required.' }}
            data={[
              {
                value: 'To-Do',
                label: 'To-Do',
              },
              {
                value: 'Email',
                label: 'Email',
              },
              {
                value: 'Letter',
                label: 'Letter',
              },
              {
                value: 'Card',
                label: 'Card',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="assignedToLogic"
            control={control}
            label="Assigned To Logic"
            showSelected={true}
            required
            rules={{ required: 'Assigned To is required.' }}
            data={[
              {
                value: 'Betterleave',
                label: 'Betterleave',
              },
              {
                value: 'User Logged In',
                label: 'User Logged In',
              },
              {
                value: 'User Who Created Event',
                label: 'User Who Created Event',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            name="templateTaskListsId"
            label="Template Task List"
            value={templateTaskList?.templateName || ''}
            required
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="templateTaskStatus"
            control={control}
            label="Template Task Status"
            required
            rules={{ required: 'Template Task Status is required.' }}
            showSelected={true}
            data={[
              {
                value: TaskStatusEnum.NotStarted,
                label: TaskStatusEnum.NotStarted,
              },
              {
                value: TaskStatusEnum.Queued,
                label: TaskStatusEnum.Queued,
              },
              {
                value: TaskStatusEnum.InProgress,
                label: TaskStatusEnum.InProgress,
              },
              {
                value: TaskStatusEnum.SentComplete,
                label: TaskStatusEnum.SentComplete,
              },
              {
                value: TaskStatusEnum.WillNotSendDo,
                label: TaskStatusEnum.WillNotSendDo,
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="timeCategory"
            label="Time Categogy"
            control={control}
            showSelected={true}
            data={[
              {
                value: 'Immediately',
                label: 'Immediately',
              },
              {
                value: 'First Week',
                label: 'First Week',
              },
              {
                value: 'Second Week',
                label: 'Second Week',
              },
              {
                value: 'Third Week',
                label: 'Third Week',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="dueDateType"
            control={control}
            label="Due Date Type"
            showSelected={true}
            data={[
              {
                value: 'Today',
                label: 'Today',
              },
              {
                value: 'Return Leave Date',
                label: 'Return Leave Date',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            name="dueDateLogic"
            label="Due Date Logic"
            control={control}
            type="number"
            InputProps={{ inputProps: { min: '0', step: '1' } }}
            onClick={() => {
              if (watch('dueDateLogic') === '0') {
                resetForm('');
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            name="orderIndex"
            label="Order Index"
            control={control}
            type="number"
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="sendTo"
            control={control}
            label="Send To"
            showSelected={true}
            data={[
              {
                value: 'Event User ID',
                label: 'Event User ID',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="mailType"
            control={control}
            label="Mail Type"
            showSelected={true}
            data={[
              {
                value: MailTypeEnum.USPSFirstClass,
                label: MailTypeEnum.USPSFirstClass,
              },
              {
                value: MailTypeEnum.USPSStandard,
                label: MailTypeEnum.USPSStandard,
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="communicationTemplate"
            control={control}
            label="Communication Template"
            showSelected={true}
            data={
              communicationTemplates?.communicationTemplates?.map((val) => {
                return {
                  value: val.id as string,
                  label: val.templateName as string,
                };
              }) || []
            }
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            name="taskCategory"
            control={control}
            label="Task Category"
            showSelected={true}
            data={[
              {
                value: 'Immediate Arrangements',
                label: 'Immediate Arrangements',
              },
              {
                value: 'Communication',
                label: 'Communication',
              },
              {
                value: 'Emotional Support',
                label: 'Emotional Support',
              },
              {
                value: 'Will & Estate',
                label: 'Will & Estate',
              },
              {
                value: 'Memorialization',
                label: 'Memorialization',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <RichTextEditorField
            name="detail"
            label="Detail"
            control={control}
            placeholder="Type Detail"
          />
        </Grid>
      </Grid>
    </form>
  );
};
